import type { DocumentValidityRuleDto } from 'korint-sdk';

type DocumentConfig = {
  category: string;
  validity?: DocumentValidityRuleDto[];
  allowMultiple?: boolean;
};

type TenantConfigWithBrokerageFirmDocuments = {
  brokerageFirmDocuments?: {
    brokerageFirmDocumentsList?: DocumentConfig[];
  };
};

export type Translate = (key: string, options?: Record<string, unknown>) => string;

// i18next tries `<key>_brokerageFirm` first, so a tenant can name a firm's document apart
export const translateBrokerageFirmDocument =
  (t: Translate): Translate =>
  (key, options) =>
    t(key, { ...options, context: 'brokerageFirm' });

export function extractBrokerageFirmDocumentConfigs(
  tenantConfig: unknown,
): DocumentConfig[] | undefined {
  return (tenantConfig as TenantConfigWithBrokerageFirmDocuments)?.brokerageFirmDocuments
    ?.brokerageFirmDocumentsList;
}

/**
 * Returns the document category label with a dynamic validity suffix
 * based on the compliance FIELD_VALIDATION rules from the config.
 *
 * - DATE_IS_NOT_OLDER_THAN → "(moins de X mois)"
 * - DATE_IS_AFTER with ref NOW → "(en cours de validité)"
 */
export function getDocumentCategoryLabel(
  category: string,
  documentConfigs: readonly DocumentConfig[] | undefined,
  t: Translate,
): string {
  const baseLabel = t(`documents.${category}`);
  if (!documentConfigs) return baseLabel;

  const config = documentConfigs.find(d => d.category === category);
  const fieldValidation = config?.validity?.find(r => r.type === 'FIELD_VALIDATION');
  if (!fieldValidation?.validations?.length) return baseLabel;

  for (const validation of fieldValidation.validations) {
    if (validation.operator === 'DATE_IS_NOT_OLDER_THAN' && validation.duration) {
      const suffix = t('documents.validity.validity.notOlderThan', {
        count: validation.duration.amount,
      });
      return `${baseLabel} ${suffix}`;
    }
    if (validation.operator === 'DATE_IS_AFTER') {
      const suffix = t('documents.validity.validity.notExpired');
      return `${baseLabel} ${suffix}`;
    }
  }

  return baseLabel;
}
